// Library Methods
import { useCallback, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Hooks
import useLegacyVFColumns from "./hooks/useLegacyVFColumns";

// Constants
import { algorithmTypes } from "src/constants/enums";

type LegacyVFColumnsType = {
  algorithmType?: string;
};

const LegacyVFColumns = ({ algorithmType }: LegacyVFColumnsType) => {
  const theme = useTheme();
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { thresholdingColumns } = useLegacyVFColumns();

  // columns to display on larger screens
  const columns = useMemo(() => {
    switch (algorithmType) {
      // visual field columns
      case algorithmTypes.FAST_THRESHOLD:
      case algorithmTypes.FULL_THRESHOLD:
      case algorithmTypes.STANDARD_THRESHOLD:
        return thresholdingColumns;

      // other columns
      default:
        return { firstColumn: [], secondColumn: [], thirdColumn: [] };
    }
  }, [algorithmType, thresholdingColumns]);

  // columns to display on smaller screens
  const mobileColumns = useMemo(
    () => [
      ...columns.firstColumn,
      ...columns.secondColumn,
      ...columns.thirdColumn,
    ],
    [columns]
  );

  const getColumnDisplay = useCallback(
    (column: {
      title: string;
      value: string | undefined;
      isBlueText?: boolean;
    }) => {
      return (
        <Typography key={column.title} fontSize="13px">
          <span style={{ fontWeight: 600 }}>{column.title}:</span>{" "}
          <span
            style={{
              color: Boolean(column.isBlueText) ? "#2563EB" : "#000",
            }}
          >
            {column.value}
          </span>
        </Typography>
      );
    },
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {smallerThanSm ? (
        <Grid container item xs={12}>
          <Grid item xs={6}>
            {mobileColumns
              ?.slice(0, Math.ceil(mobileColumns?.length / 2))
              .map((column) => getColumnDisplay(column))}
          </Grid>
          <Grid item xs={6}>
            {mobileColumns
              ?.slice(Math.ceil(mobileColumns?.length / 2))
              .map((column) => getColumnDisplay(column))}
          </Grid>
        </Grid>
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={4}>
            {columns?.firstColumn?.map((column) => getColumnDisplay(column))}
          </Grid>
          <Grid item xs={4}>
            {columns?.secondColumn?.map((column) => getColumnDisplay(column))}
          </Grid>
          <Grid item xs={4}>
            {columns?.thirdColumn?.map((column) => getColumnDisplay(column))}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default LegacyVFColumns;
